import { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Send, X, Paperclip, Loader2, MessageCircle } from "lucide-react";
import { MessageAttachments, annotateAttachmentUrl } from "@/components/message-attachments";
import type { TodoComment } from "@shared/schema";
import { uploadMimeType } from "@/lib/upload-mime";

// Task #3973 — shared internal comment-thread primitives. These started life
// inside maintenance-task-detail.tsx (task comments) and are extracted here so
// the to-do comment thread can reuse the identical composer/attachment/mention
// UX without forking the code. Maintenance imports the primitives and keeps its
// own query/mutation; the to-do board uses the self-contained `TodoCommentsThread`.

// Picture/video uploads on comments. Same presigned upload pattern the messaging
// composer uses (POST upload-url → PUT binary → submit /objects/* path with the
// chosen filename annotated as `?fn=`) — 25 MB cap for images/PDFs, 100 MB cap
// for videos.
const TASK_ATTACH_MAX_BYTES = 25 * 1024 * 1024;
const TASK_ATTACH_VIDEO_MAX_BYTES = 100 * 1024 * 1024;
const TASK_ATTACH_MAX_COUNT = 10;
const TASK_ATTACH_ALLOWED = new Set([
  "image/jpeg", "image/png", "image/webp", "image/gif", "image/heic", "image/heif",
  "application/pdf",
  "video/mp4", "video/quicktime", "video/webm", "video/mpeg", "video/3gpp",
]);

export type PendingTaskAttachment = {
  id: string;
  name: string;
  status: "uploading" | "ready" | "error";
  objectPath?: string;
  errorMessage?: string;
};

export function makeAttachId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// A team member as far as @-mentions + author display are concerned. Both the
// maintenance task list (schema `TeamMember`) and the to-do board's local team
// row are structurally assignable to this minimal shape.
export type MentionTeamMember = { id: number; name?: string | null; email: string };

// The minimal comment shape the thread renders. Both `TaskComment` and
// `TodoComment` satisfy it.
export type CommentLike = {
  id: number;
  authorId: number;
  content: string;
  createdAt: string | Date;
  attachmentUrls?: string[] | null;
};

// Composer chip-row + hidden file input. Calls onChange with the staged
// attachments so the parent can build the final attachmentUrls array on
// submit (annotate /objects/<uuid> with ?fn=<original-name> just like the
// chat composer does so the lightbox Download saves the human filename).
export function AttachmentComposer({
  attachments, setAttachments, disabled, testIdPrefix, uploadUrlEndpoint = "/api/app/tasks/upload-url",
}: {
  attachments: PendingTaskAttachment[];
  setAttachments: (updater: (prev: PendingTaskAttachment[]) => PendingTaskAttachment[]) => void;
  disabled?: boolean;
  testIdPrefix: string;
  uploadUrlEndpoint?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  async function onPick(files: FileList | null) {
    if (!files) return;
    const remaining = TASK_ATTACH_MAX_COUNT - attachments.length;
    if (remaining <= 0) {
      toast({ title: "Attachment limit reached", description: `You can attach up to ${TASK_ATTACH_MAX_COUNT} files.`, variant: "destructive" });
      return;
    }
    const list = Array.from(files).slice(0, remaining);
    for (const file of list) {
      const id = makeAttachId();
      // Resolve BEFORE the allow-list check: TASK_ATTACH_ALLOWED already
      // permits image/heic, but browsers report an EMPTY file.type for .heic
      // picks, so has("") rejected a photo the list accepts. The same resolved
      // value drives the video size cap below and the PUT header.
      const resolvedMime = uploadMimeType(file);
      if (!TASK_ATTACH_ALLOWED.has(resolvedMime)) {
        setAttachments((prev) => [...prev, { id, name: file.name, status: "error", errorMessage: "Only images, videos and PDFs are allowed" }]);
        continue;
      }
      const isVideo = resolvedMime.startsWith("video/");
      const capBytes = isVideo ? TASK_ATTACH_VIDEO_MAX_BYTES : TASK_ATTACH_MAX_BYTES;
      if (file.size > capBytes) {
        const cap = Math.round(capBytes / (1024 * 1024));
        const msg = isVideo
          ? `Video too large (${cap} MB max) — trim or compress it on your phone before re-sending`
          : `File is over ${cap} MB`;
        setAttachments((prev) => [...prev, { id, name: file.name, status: "error", errorMessage: msg }]);
        continue;
      }
      setAttachments((prev) => [...prev, { id, name: file.name, status: "uploading" }]);
      try {
        const urlRes = await apiRequest("POST", uploadUrlEndpoint, {});
        const { uploadUrl, objectPath } = await urlRes.json();
        await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": resolvedMime }, body: file });
        setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, status: "ready", objectPath } : a)));
      } catch (err) {
        setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, status: "error", errorMessage: parseErrorMessage(err, "Upload failed") } : a)));
      }
    }
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <div className="space-y-1.5">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,video/*,application/pdf"
        className="hidden"
        onChange={(e) => onPick(e.target.files)}
        data-testid={`${testIdPrefix}-input`}
      />
      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={disabled || attachments.length >= TASK_ATTACH_MAX_COUNT}
          onClick={() => inputRef.current?.click()}
          data-testid={`${testIdPrefix}-button`}
        >
          <Paperclip className="h-3 w-3 mr-1" />
          Attach
        </Button>
        {attachments.map((a) => (
          <span
            key={a.id}
            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] border ${a.status === "error" ? "bg-red-50 border-red-200 text-red-700" : "bg-gray-50 border-gray-200 text-gray-700"}`}
            data-testid={`${testIdPrefix}-chip-${a.id}`}
          >
            {a.status === "uploading" && <Loader2 className="h-3 w-3 animate-spin" />}
            {a.status === "error" && a.errorMessage ? (
              <span className="flex flex-col max-w-[260px]">
                <span className="truncate font-medium" title={a.name}>{a.name}</span>
                <span className="truncate opacity-90" title={a.errorMessage}>{a.errorMessage}</span>
              </span>
            ) : (
              <span className="truncate max-w-[140px]" title={a.name}>{a.name}</span>
            )}
            <button
              type="button"
              onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Remove attachment"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

export function buildReadyAttachmentUrls(attachments: PendingTaskAttachment[]): string[] {
  return attachments
    .filter((a) => a.status === "ready" && a.objectPath)
    // Existing attachments come back already annotated (`?ct=...&fn=...`) when
    // we hydrate the editor from the task; only freshly-uploaded paths need
    // the filename annotation applied.
    .map((a) => (a.objectPath!.includes("?") ? a.objectPath! : annotateAttachmentUrl(a.objectPath!, a.name)));
}

// Match every accepted teammate whose display name (or email) appears in the
// text as `@Name`, returning their mention ids (team-member row ids). De-duped.
export function extractMentions(content: string, members: MentionTeamMember[]): number[] {
  const mentioned: number[] = [];
  for (const member of members) {
    const nameToMatch = member.name || member.email;
    if (nameToMatch && content.includes(`@${nameToMatch}`)) {
      mentioned.push(member.id);
    }
  }
  return Array.from(new Set(mentioned));
}

export function CommentItem({ comment, teamMembers, currentUser, isFocused, resolveAuthorName }: {
  comment: CommentLike;
  teamMembers: MentionTeamMember[];
  currentUser?: { id: number; name?: string | null; email: string };
  isFocused?: boolean;
  // Optional override that maps an authorId (app-user id) to a display name —
  // used where the author id domain differs from the mention/team-member id
  // domain (e.g. the to-do thread). When omitted we fall back to matching the
  // team-member row id, then the current user (the maintenance behavior).
  resolveAuthorName?: (authorId: number) => string | null | undefined;
}) {
  const overrideName = resolveAuthorName?.(comment.authorId);
  const teamAuthor = teamMembers.find((m) => m.id === comment.authorId);
  const isCurrentUser = currentUser && currentUser.id === comment.authorId;
  const authorName = overrideName
    || teamAuthor?.name || teamAuthor?.email
    || (isCurrentUser ? (currentUser.name || currentUser.email) : null)
    || "Unknown";
  const initials = authorName[0].toUpperCase();

  // Scroll into view + briefly highlight when arriving from a mention bell
  // notification deep link (?comment=N). Initialize from isFocused so the ring
  // is present on the very first paint of a focused comment — deriving the
  // initial value (rather than flipping it on via a delayed setHighlight(true))
  // makes the highlight robust to re-renders / re-mounts: every focused instance
  // paints highlighted immediately. The ring then fades after ~2.4s.
  const itemRef = useRef<HTMLDivElement | null>(null);
  const [highlight, setHighlight] = useState<boolean>(!!isFocused);
  useEffect(() => {
    if (!isFocused) return;
    setHighlight(true);
    const node = itemRef.current;
    const t1 = setTimeout(() => {
      node?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
    const t2 = setTimeout(() => setHighlight(false), 2400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isFocused, comment.id]);

  function renderContentParts(content: string) {
    const parts: Array<{ type: "text" | "mention"; value: string }> = [];
    let lastIndex = 0;
    const regex = /@(\w[\w\s]*)/g;
    let match;
    while ((match = regex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: "text", value: content.slice(lastIndex, match.index) });
      }
      parts.push({ type: "mention", value: match[1] });
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < content.length) {
      parts.push({ type: "text", value: content.slice(lastIndex) });
    }
    return parts;
  }

  return (
    <div ref={itemRef} className={`flex gap-3 py-3 border-b border-gray-100 last:border-b-0 transition-all duration-500 ${highlight ? "rounded-md ring-1 ring-purple-400 bg-purple-50" : ""}`} data-testid={`comment-${comment.id}`}>
      <div className="h-7 w-7 rounded-full bg-brand flex items-center justify-center text-white text-xs font-medium shrink-0">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium text-gray-900">{authorName}</span>
          <span className="text-xs text-gray-400">{format(new Date(comment.createdAt), "MMM d, h:mm a")}</span>
        </div>
        <p className="text-sm text-gray-700 mt-0.5">
          {renderContentParts(comment.content).map((part, i) =>
            part.type === "mention" ? (
              <strong key={i} className="text-blue-600">@{part.value}</strong>
            ) : (
              <span key={i}>{part.value}</span>
            )
          )}
        </p>
        {comment.attachmentUrls && comment.attachmentUrls.length > 0 && (
          <MessageAttachments urls={comment.attachmentUrls} messageId={`comment-${comment.id}`} />
        )}
      </div>
    </div>
  );
}

export function MentionTextarea({ value, onChange, teamMembers, placeholder, testId = "input-comment" }: {
  value: string;
  onChange: (val: string) => void;
  teamMembers: MentionTeamMember[];
  placeholder?: string;
  testId?: string;
}) {
  const [mentionSearch, setMentionSearch] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [mentionStart, setMentionStart] = useState(-1);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const filtered = teamMembers.filter((m) =>
    String(m.name ?? "").toLowerCase().includes(mentionSearch.toLowerCase()) ||
    String(m.email ?? "").toLowerCase().includes(mentionSearch.toLowerCase())
  );

  function handleInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const val = e.target.value;
    onChange(val);
    const cursor = e.target.selectionStart;
    const lastAt = val.lastIndexOf("@", cursor - 1);
    if (lastAt >= 0) {
      const fragment = val.slice(lastAt + 1, cursor);
      if (!fragment.includes(" ")) {
        setMentionStart(lastAt);
        setMentionSearch(fragment);
        setShowMentions(true);
        return;
      }
    }
    setShowMentions(false);
  }

  function insertMention(member: MentionTeamMember) {
    const name = member.name || member.email;
    const before = value.slice(0, mentionStart);
    const after = value.slice(mentionStart + 1 + mentionSearch.length);
    const newVal = `${before}@${name} ${after}`;
    onChange(newVal);
    setShowMentions(false);
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = before.length + name.length + 2;
        textareaRef.current.selectionStart = pos;
        textareaRef.current.selectionEnd = pos;
        textareaRef.current.focus();
      }
    }, 0);
  }

  return (
    <div className="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        placeholder={placeholder}
        rows={3}
        className="resize-none"
        data-testid={testId}
      />
      {showMentions && filtered.length > 0 && (
        <div className="absolute bottom-full left-0 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-56 mb-1">
          {filtered.map((m) => (
            <button
              key={m.id}
              type="button"
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2"
              onClick={() => insertMention(m)}
            >
              <div className="h-5 w-5 rounded-full bg-brand text-white text-xs flex items-center justify-center">
                {(m.name || m.email || "U")[0].toUpperCase()}
              </div>
              <span>{m.name || m.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Self-contained comment thread for a single to-do. Owns its own query +
// post mutation; mirrors the maintenance task comment UX (composer, @-mentions,
// attachments, deep-link highlight) via the shared primitives above.
export function TodoCommentsThread({
  todoId,
  mentionMembers,
  authorNameByUserId,
  currentUser,
  focusedCommentId,
  readOnly,
  onBusyChange,
}: {
  todoId: number;
  // Accepted teammates for the @-mention picker; `id` is the team-member row id
  // the POST body expects in `mentions`.
  mentionMembers: MentionTeamMember[];
  // app-user id → display name, for resolving comment authors (whose id is an
  // app-user id, a different domain than the mention id).
  authorNameByUserId: Map<number, string>;
  currentUser?: { id: number; name?: string | null; email: string };
  focusedCommentId?: number | null;
  readOnly?: boolean;
  // Reports whether a post or upload is in flight so a parent dialog can gate
  // its dismissal (the mutation lives here, not in the parent).
  onBusyChange?: (busy: boolean) => void;
}) {
  const { toast } = useToast();
  const [commentText, setCommentText] = useState("");
  const [attachments, setAttachments] = useState<PendingTaskAttachment[]>([]);

  const commentsQuery = useQuery<TodoComment[]>({
    queryKey: ["/api/app/todos", todoId, "comments"],
    queryFn: () => apiRequest("GET", `/api/app/todos/${todoId}/comments`).then((r) => r.json()),
  });
  const comments = Array.isArray(commentsQuery.data) ? commentsQuery.data : [];

  const resolveAuthorName = useMemo(
    () => (authorId: number) => authorNameByUserId.get(authorId),
    [authorNameByUserId],
  );

  const postMutation = useMutation({
    mutationFn: (vars: { content: string; mentions: number[]; attachmentUrls: string[] }) =>
      apiRequest("POST", `/api/app/todos/${todoId}/comments`, vars).then((r) => r.json()),
    onSuccess: () => {
      setCommentText("");
      setAttachments([]);
      queryClient.invalidateQueries({ queryKey: ["/api/app/todos", todoId, "comments"] });
      // A new mention may have produced a bell row / inbox item for a teammate.
      queryClient.invalidateQueries({ queryKey: ["/api/app/team-inbox"] });
    },
    onError: (err) =>
      toast({
        title: "Failed to post comment",
        description: parseErrorMessage(err, "Please try again."),
        variant: "destructive",
      }),
  });

  function handlePost() {
    const readyAttachments = attachments.filter((a) => a.status === "ready");
    if (!commentText.trim() && readyAttachments.length === 0) return;
    if (attachments.some((a) => a.status === "uploading")) {
      toast({ title: "Wait for uploads to finish" });
      return;
    }
    postMutation.mutate({
      content: commentText,
      mentions: extractMentions(commentText, mentionMembers),
      attachmentUrls: buildReadyAttachmentUrls(attachments),
    });
  }

  const isUploading = attachments.some((a) => a.status === "uploading");
  useEffect(() => {
    onBusyChange?.(postMutation.isPending || isUploading);
  }, [postMutation.isPending, isUploading, onBusyChange]);

  return (
    <div className="space-y-3" data-testid="todo-comments-thread">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <MessageCircle className="h-4 w-4" />
        Comments
      </div>

      {commentsQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2" data-testid="text-todo-comments-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading comments…
        </div>
      ) : comments.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2" data-testid="text-todo-comments-empty">
          No comments yet.
        </p>
      ) : (
        <div className="divide-y divide-gray-100" data-testid="list-todo-comments">
          {comments.map((c) => (
            <CommentItem
              key={c.id}
              comment={c}
              teamMembers={mentionMembers}
              currentUser={currentUser}
              resolveAuthorName={resolveAuthorName}
              isFocused={focusedCommentId != null && c.id === focusedCommentId}
            />
          ))}
        </div>
      )}

      {!readOnly && (
        <div className="space-y-2 pt-1">
          <MentionTextarea
            value={commentText}
            onChange={setCommentText}
            teamMembers={mentionMembers}
            placeholder="Add a comment… use @ to mention a teammate"
            testId="input-todo-comment"
          />
          <AttachmentComposer
            attachments={attachments}
            setAttachments={setAttachments}
            disabled={postMutation.isPending}
            testIdPrefix="todo-comment-attach"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handlePost}
              disabled={postMutation.isPending || (!commentText.trim() && attachments.filter((a) => a.status === "ready").length === 0)}
              data-testid="button-post-todo-comment"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {postMutation.isPending ? "Posting…" : "Post"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
